import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Copy, ExternalLink, Settings2, Trash2, ShieldAlert, ShieldCheck, Shield, Loader2, Baby, QrCode, Code2, Sparkles, Reply, Wand2, Download, Cake, Heart, Trophy } from "lucide-react";
import { api, APP_ORIGIN } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import { FilterTabs, timeAgo } from "./engShared";

const STATUS_TABS = [["new", "New"], ["reviewing", "Reviewing"], ["approved", "Approved"],
  ["needs_editing", "Needs Editing"], ["ready_for_air", "Ready for Air"], ["rejected", "Rejected"],
  ["archived", "Archived"], ["all", "All"]];
const STATUS_LABELS = { new: "New", reviewing: "Reviewing", approved: "Approved", rejected: "Rejected",
  needs_editing: "Needs Editing", ready_for_air: "Ready for Air", scheduled: "Scheduled",
  sent_to_playout: "Sent to Playout", played: "Played", archived: "Archived" };

const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Clipboard blocked — select and copy the text manually");
  }
};

const ModBadge = ({ s }) => {
  if (s.moderation_status === "pending")
    return <span className="inline-flex items-center gap-1 rounded-full border border-line px-2 py-0.5 text-[10px] text-ink-muted" data-testid={`voice-mod-${s.submission_id}`}><Loader2 className="h-3 w-3 animate-spin" /> AI checking…</span>;
  const map = {
    clean: ["Clean", "border-emerald-400/40 bg-emerald-400/10 text-emerald-300", ShieldCheck],
    review: ["Review Recommended", "border-amber-400/40 bg-amber-400/10 text-amber-300", Shield],
    high_risk: ["HIGH RISK", "border-rose-400/50 bg-rose-400/15 text-rose-300", ShieldAlert],
  };
  const [label, cls, Icon] = map[s.moderation_status] || map.review;
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${cls}`} data-testid={`voice-mod-${s.submission_id}`}><Icon className="h-3 w-3" />{label}</span>;
};

function SettingsPanel() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/voice/settings").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return null;
  const save = async (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    try {
      await api.put("/voice/settings", {
        enabled: next.enabled, welcome_message: next.welcome_message,
        enabled_categories: next.enabled_categories,
        voice_wall_enabled: !!next.voice_wall_enabled,
        max_recording_seconds: Number(next.max_recording_seconds) || 60 });
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="rounded-xl border border-line bg-bg-surface p-4 space-y-3" data-testid="voice-settings">
      <label className="flex items-center justify-between text-sm font-bold">
        Accept listener voice messages
        <input type="checkbox" checked={!!cfg.enabled} data-testid="voice-set-enabled"
          onChange={(e) => save({ enabled: e.target.checked })} />
      </label>
      <label className="flex items-center justify-between gap-3 text-sm font-bold">
        <span>Public voice wall
          <span className="block text-[10px] font-normal text-ink-muted">Approved clips playable on your public Engage page</span>
        </span>
        <input type="checkbox" checked={!!cfg.voice_wall_enabled} data-testid="voice-set-wall"
          onChange={(e) => save({ voice_wall_enabled: e.target.checked })} />
      </label>
      <div className="grid sm:grid-cols-[1fr_140px] gap-2">
        <input value={cfg.welcome_message} onChange={(e) => setCfg({ ...cfg, welcome_message: e.target.value })}
          onBlur={() => save({})} data-testid="voice-set-welcome" maxLength={300}
          className="rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm" />
        <div className="flex items-center gap-2">
          <input type="number" min={10} max={300} value={cfg.max_recording_seconds} data-testid="voice-set-max"
            onChange={(e) => save({ max_recording_seconds: e.target.value })}
            className="w-20 rounded-md border border-line bg-bg-elevated px-2 py-2 text-sm" />
          <span className="text-[10px] font-mono uppercase text-ink-muted">sec max</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {(cfg.all_categories || []).map((c) => {
          const on = cfg.enabled_categories.includes(c.key);
          return (
            <button key={c.key} data-testid={`voice-set-cat-${c.key}`}
              onClick={() => save({ enabled_categories: on ? cfg.enabled_categories.filter((x) => x !== c.key) : [...cfg.enabled_categories, c.key] })}
              className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition ${on ? "border-brand-amber/50 bg-brand-amber/10 text-brand-amber" : "border-line text-ink-muted"}`}>
              {c.label}{on ? " ✓" : ""}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SharePanel({ publicUrl }) {
  const embed = `<iframe src="${publicUrl}?embed=1" width="420" height="640" style="border:0;border-radius:16px" title="Send us your voice"></iframe>`;
  return (
    <div className="rounded-xl border border-line bg-bg-surface p-4 grid sm:grid-cols-[auto_1fr] gap-4" data-testid="voice-share-panel">
      <div className="text-center">
        <img src={`${APP_ORIGIN}/api/voice/qr?base=${encodeURIComponent(window.location.origin)}`}
          alt="Voice page QR" className="h-36 w-36 rounded-lg border border-line bg-white p-1" data-testid="voice-qr-img" />
        <div className="mt-1 font-mono text-[9px] uppercase tracking-wider text-ink-muted">Scan → record</div>
      </div>
      <div className="space-y-2 min-w-0">
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted flex items-center gap-1"><QrCode className="h-3 w-3" /> QR &amp; website widget</div>
        <p className="text-[11px] text-ink-muted">Print the QR on flyers and studio posters, or paste this snippet into your station website to embed the recorder:</p>
        <div className="flex gap-2">
          <code className="flex-1 min-w-0 truncate rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-[10px] text-ink-secondary" data-testid="voice-embed-code">{embed}</code>
          <button onClick={() => copyText(embed, "Embed code")}
            data-testid="voice-copy-embed"
            className="inline-flex items-center gap-1 rounded-md border border-line px-3 py-1.5 text-xs font-bold shrink-0 hover:border-brand-amber/50"><Code2 className="h-3 w-3" /> Copy</button>
        </div>
      </div>
    </div>
  );
}

function ProduceIntro({ s, voices, onDone }) {
  const [voice, setVoice] = useState("echo");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/voice/submissions/${s.submission_id}/generate-intro`, { voice });
      toast.success(`Produced with intro: "${r.data.intro_script.slice(0, 60)}…"`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Production failed"); }
    setBusy(false);
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      <select value={voice} onChange={(e) => setVoice(e.target.value)} data-testid={`voice-intro-voice-${s.submission_id}`}
        className="rounded-md border border-line bg-bg-elevated px-1.5 py-1 text-[10px] max-w-[130px]">
        {voices.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
      </select>
      <button onClick={go} disabled={busy} data-testid={`voice-produce-${s.submission_id}`}
        className="inline-flex items-center gap-1 rounded-full border border-violet-400/40 bg-violet-400/10 px-3 py-1 text-[11px] font-bold text-violet-300 disabled:opacity-50">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />} AI Intro
      </button>
    </span>
  );
}

export default function EngVoiceInbox() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], counts: {}, types: {} });
  const [status, setStatus] = useState("new");
  const [stype, setStype] = useState("all");
  const [panel, setPanel] = useState(""); // "" | settings | share
  const [drafts, setDrafts] = useState({});
  const [voices, setVoices] = useState([]);
  const [suggesting, setSuggesting] = useState("");
  const replyInputRef = useRef(null);
  const replyTargetRef = useRef(null);
  const publicUrl = `${window.location.origin}/voice/${user?.station_id || ""}`;

  const load = (s = status, t = stype) =>
    api.get(`/voice/submissions?status=${s}&stype=${t}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(status, stype); }, [status, stype]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/voice/intro-voices").then((r) => setVoices(r.data.voices || [])).catch(() => {}); }, []);
  useEffect(() => {
    const id = setInterval(() => load(), 20000);
    return () => clearInterval(id);
  }, [status, stype]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = async (id, body, msg, nextTab) => {
    try {
      await api.patch(`/voice/submissions/${id}`, body);
      msg && toast.success(msg);
      if (nextTab && nextTab !== status) setStatus(nextTab);
      else load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const reprocess = async (id) => {
    try { await api.post(`/voice/submissions/${id}/reprocess`); toast.success("AI check re-queued"); setTimeout(load, 8000); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    try { await api.delete(`/voice/submissions/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const suggest = async (id) => {
    setSuggesting(id);
    try { await api.post(`/voice/submissions/${id}/suggest-reply`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setSuggesting("");
  };
  const pickReply = (id) => { replyTargetRef.current = id; replyInputRef.current?.click(); };
  const uploadReply = async (e) => {
    const f = e.target.files?.[0];
    const id = replyTargetRef.current;
    e.target.value = "";
    if (!f || !id) return;
    const fd = new FormData();
    fd.append("audio", f);
    try { await api.post(`/voice/submissions/${id}/reply`, fd); toast.success("DJ reply attached"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
  };

  const isBirthdayToday = (d) => d && d.slice(5) === new Date().toISOString().slice(5, 10);

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto space-y-5" data-testid="eng-voice-inbox">
      <PageHeader eyebrow="// ENGAGEMENT · LISTENER VOICE INBOX" title="Voice Inbox"
        description="Listener recordings — transcribed and AI-checked. Nothing airs without your approval." />

      <input ref={replyInputRef} type="file" accept="audio/*" className="hidden" onChange={uploadReply} data-testid="voice-reply-input" />

      <div className="rounded-xl border border-line bg-bg-surface p-4 flex flex-wrap items-center gap-3" data-testid="voice-share-box">
        <Mic className="h-4 w-4 text-brand-amber shrink-0" />
        <code className="text-xs text-ink-secondary flex-1 min-w-0 truncate" data-testid="voice-public-url">{publicUrl}</code>
        <button onClick={() => copyText(publicUrl, "Link")}
          data-testid="voice-copy-link"
          className="inline-flex items-center gap-1 rounded-md border border-line px-3 py-1.5 text-xs font-bold hover:border-brand-amber/50"><Copy className="h-3 w-3" /> Copy</button>
        <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="voice-open-public"
          className="inline-flex items-center gap-1 rounded-md bg-brand-amber px-3 py-1.5 text-xs font-bold text-black"><ExternalLink className="h-3 w-3" /> Open</a>
        <button onClick={() => setPanel(panel === "share" ? "" : "share")} data-testid="voice-toggle-share"
          className="inline-flex items-center gap-1 rounded-md border border-line px-3 py-1.5 text-xs font-bold"><QrCode className="h-3 w-3" /> QR &amp; Widget</button>
        <button onClick={() => setPanel(panel === "settings" ? "" : "settings")} data-testid="voice-toggle-settings"
          className="inline-flex items-center gap-1 rounded-md border border-line px-3 py-1.5 text-xs font-bold"><Settings2 className="h-3 w-3" /> Settings</button>
      </div>
      {panel === "settings" && <SettingsPanel />}
      {panel === "share" && <SharePanel publicUrl={publicUrl} />}

      <div className="flex flex-wrap items-center gap-3">
        <FilterTabs tabs={STATUS_TABS} active={status} onChange={setStatus} counts={data.counts} />
        <select value={stype} onChange={(e) => setStype(e.target.value)} data-testid="voice-type-filter"
          className="ml-auto rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs">
          <option value="all">All types</option>
          {Object.entries(data.types || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {!data.items.length && (
        <div className="rounded-xl border border-dashed border-line p-10 text-center text-xs text-ink-muted" data-testid="voice-empty">
          No {status === "all" ? "" : STATUS_LABELS[status]?.toLowerCase() + " "}voice messages{stype !== "all" ? ` of that type` : ""} yet — share your link or QR to start collecting listener audio.
        </div>
      )}

      {data.items.map((s) => (
        <div key={s.submission_id} className="rounded-xl border border-line bg-bg-surface p-4 space-y-3" data-testid={`voice-card-${s.submission_id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-bold">{s.submitter_name}</span>
            <span className="rounded-full border border-brand-cyan/40 bg-brand-cyan/10 px-2 py-0.5 text-[10px] font-bold text-brand-cyan">{data.types?.[s.submission_type] || s.submission_type}</span>
            <ModBadge s={s} />
            {s.possible_minor && <span className="inline-flex items-center gap-1 rounded-full border border-violet-400/40 bg-violet-400/10 px-2 py-0.5 text-[10px] font-bold text-violet-300"><Baby className="h-3 w-3" /> Possible minor — extra care</span>}
            {s.submission_type === "birthday" && s.birthday_date && (
              <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${isBirthdayToday(s.birthday_date) ? "border-pink-400/50 bg-pink-400/15 text-pink-300" : "border-line text-ink-muted"}`} data-testid={`voice-bday-${s.submission_id}`}>
                <Cake className="h-3 w-3" /> {isBirthdayToday(s.birthday_date) ? "TODAY!" : s.birthday_date}
              </span>
            )}
            {s.contest_title && <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[10px] font-bold text-amber-300"><Trophy className="h-3 w-3" /> {s.contest_title}</span>}
            <span className="ml-auto text-[10px] text-ink-muted">
              {s.show ? `${s.show} · ` : ""}{s.submitter_location ? `${s.submitter_location} · ` : ""}
              {s.duration ? `${Math.round(s.duration)}s · ` : ""}{timeAgo(s.created_at)}
            </span>
          </div>
          {(s.dedication_song || s.dedicated_to || s.birthday_person) && (
            <div className="text-[11px] text-ink-secondary flex items-center gap-1.5" data-testid={`voice-extra-${s.submission_id}`}>
              <Heart className="h-3 w-3 text-pink-400" />
              {s.submission_type === "dedication"
                ? <>“{s.dedication_song || "song of their choice"}” dedicated to <b>{s.dedicated_to || "someone special"}</b></>
                : <>Birthday wish for <b>{s.birthday_person}</b></>}
            </div>
          )}
          <audio controls preload="none" src={`${APP_ORIGIN}/api/voice/submissions/${s.submission_id}/audio`}
            className="w-full h-9" data-testid={`voice-audio-${s.submission_id}`} />
          {s.has_produced && (
            <div className="rounded-lg border border-violet-400/30 bg-violet-400/5 p-2.5 space-y-1.5" data-testid={`voice-produced-${s.submission_id}`}>
              <div className="flex items-center gap-2 text-[10px] font-bold text-violet-300">
                <Sparkles className="h-3 w-3" /> Produced with AI intro{s.intro_script ? `: “${s.intro_script.slice(0, 90)}”` : ""}
                <a href={`${APP_ORIGIN}/api/voice/submissions/${s.submission_id}/produced-audio`} download={`${s.submitter_name}-produced.mp3`}
                  data-testid={`voice-produced-dl-${s.submission_id}`}
                  className="ml-auto inline-flex items-center gap-1 rounded-md border border-violet-400/40 px-2 py-1 text-violet-300"><Download className="h-3 w-3" /> MP3</a>
              </div>
              <audio controls preload="none" src={`${APP_ORIGIN}/api/voice/submissions/${s.submission_id}/produced-audio`} className="w-full h-8" />
            </div>
          )}
          {s.has_reply && (
            <div className="rounded-lg border border-brand-cyan/30 bg-brand-cyan/5 p-2.5 space-y-1.5" data-testid={`voice-reply-block-${s.submission_id}`}>
              <div className="text-[10px] font-bold text-brand-cyan flex items-center gap-1"><Reply className="h-3 w-3" /> DJ reply attached</div>
              <audio controls preload="none" src={`${APP_ORIGIN}/api/voice/submissions/${s.submission_id}/reply-audio`} className="w-full h-8" />
            </div>
          )}
          {s.suggested_reply && (
            <div className="rounded-lg border border-line bg-bg-elevated/60 p-2.5 text-[11px] text-ink-secondary" data-testid={`voice-suggestion-${s.submission_id}`}>
              <span className="font-bold text-brand-amber">AI suggested reply:</span> {s.suggested_reply}
            </div>
          )}
          {!!(s.moderation_flags || []).length && (
            <div className="text-[10px] text-amber-300/90" data-testid={`voice-flags-${s.submission_id}`}>
              Flags: {s.moderation_flags.map((f) => `${f.type}${f.note ? ` (${f.note})` : ""}`).join(" · ")}
            </div>
          )}
          <textarea rows={2} value={drafts[s.submission_id] ?? s.transcript ?? ""}
            onChange={(e) => setDrafts({ ...drafts, [s.submission_id]: e.target.value })}
            onBlur={() => {
              const v = drafts[s.submission_id];
              if (v !== undefined && v !== s.transcript) patch(s.submission_id, { transcript: v }, "Transcript saved");
            }}
            placeholder={s.moderation_status === "pending" ? "Transcribing…" : "No transcript — add one"}
            data-testid={`voice-transcript-${s.submission_id}`}
            className="w-full rounded-md border border-line bg-bg-elevated px-3 py-2 text-xs font-mono" />
          <div className="flex flex-wrap items-center gap-2">
            {["new", "reviewing", "needs_editing"].includes(s.status) && (
              <button onClick={() => patch(s.submission_id, { status: "approved" }, "Approved — moved to the Approved tab", "approved")} data-testid={`voice-approve-${s.submission_id}`}
                className="rounded-full border border-emerald-400/40 bg-emerald-400/10 px-3 py-1 text-[11px] font-bold text-emerald-300">Approve</button>
            )}
            {s.status === "approved" && (
              <button onClick={() => patch(s.submission_id, { status: "ready_for_air" }, "Marked ready for air")} data-testid={`voice-ready-${s.submission_id}`}
                className="rounded-full border border-brand-cyan/40 bg-brand-cyan/10 px-3 py-1 text-[11px] font-bold text-brand-cyan">Ready for Air</button>
            )}
            {!["rejected", "archived"].includes(s.status) && (
              <button onClick={() => patch(s.submission_id, { status: "rejected" }, "Rejected")} data-testid={`voice-reject-${s.submission_id}`}
                className="rounded-full border border-rose-400/40 px-3 py-1 text-[11px] font-bold text-rose-300">Reject</button>
            )}
            {s.moderation_status === "pending" && (
              <button onClick={() => reprocess(s.submission_id)} data-testid={`voice-reprocess-${s.submission_id}`}
                className="rounded-full border border-line px-3 py-1 text-[11px] font-bold text-ink-muted hover:text-ink-primary">Re-run AI check</button>
            )}
            {s.submission_type === "ask_dj" && (
              <>
                <button onClick={() => suggest(s.submission_id)} disabled={suggesting === s.submission_id} data-testid={`voice-suggest-${s.submission_id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-brand-amber/40 bg-brand-amber/10 px-3 py-1 text-[11px] font-bold text-brand-amber disabled:opacity-50">
                  {suggesting === s.submission_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Suggest reply
                </button>
                <button onClick={() => pickReply(s.submission_id)} data-testid={`voice-attach-reply-${s.submission_id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-brand-cyan/40 px-3 py-1 text-[11px] font-bold text-brand-cyan">
                  <Reply className="h-3 w-3" /> {s.has_reply ? "Replace reply" : "Attach DJ reply"}
                </button>
              </>
            )}
            {voices.length > 0 && ["approved", "ready_for_air", "reviewing", "needs_editing"].includes(s.status) && (
              <ProduceIntro s={s} voices={voices} onDone={load} />
            )}
            <select value={s.status} onChange={(e) => patch(s.submission_id, { status: e.target.value })}
              data-testid={`voice-status-${s.submission_id}`}
              className="rounded-md border border-line bg-bg-elevated px-2 py-1 text-[11px]">
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <button onClick={() => del(s.submission_id)} data-testid={`voice-delete-${s.submission_id}`}
              className="ml-auto rounded border border-line p-1.5 text-ink-muted hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}
